import {copilotDockedPageEndedEvent, copilotDockedPageReadyEvent} from '@github-ui/copilot-chat-helpers/constants'
import {clearDockedPageReady, publishDockedPageReady} from '@github-ui/copilot-chat-helpers/docked-page-store'
import {readPersistedChatOpenState} from '@github-ui/copilot-chat-helpers/embedded-chat-open-state'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {updateUrl} from '@github-ui/history'
import {createContext, use, useEffect, useMemo, useReducer, useRef, type ReactNode, type RefObject} from 'react'

import {measureIsWideViewport, isWideWidth, DIFF_COMPARISON_VIEWER_CONTAINER_ID} from '../helpers/measure-viewport'
import type {ViewMode} from '../components/comments/CommentsContext'

export const DockedPanel = {
  AgentSessions: 'agentSessions',
  Comments: 'comments',
  CopilotChat: 'copilotChat',
  Alerts: 'alerts',
  Timeline: 'timeline',
  Overview: 'overview',
  MergeStatus: 'mergeStatus',
  ManageBatchReviewComments: 'manageBatchReviewComments',
} as const

export type DockedPanel = (typeof DockedPanel)[keyof typeof DockedPanel]
export type NullableDockedPanel = DockedPanel | null

export interface CommentsPanelMetadata {
  selectedAuthor?: string
  hideResolved?: boolean
  viewMode?: ViewMode
}

export type TimelineActivityGroupKey =
  | 'commits'
  | 'baseForce'
  | 'approvals'
  | 'changesRequested'
  | 'comments'
  | 'issueComments'
  | 'state'
  | 'labels'
  | 'reviewRequests'
  | 'description'

export interface TimelineActivityGroupMetadata {
  label: string
  group: TimelineActivityGroupKey
  since?: string
  expectedCount?: number
}

export interface TimelinePanelMetadata {
  /** When set, the Timeline panel renders just this activity group's events. */
  activityGroup?: TimelineActivityGroupMetadata
}

/**
 * Entry point that opened the Manage batch panel.
 *
 * Carried in panel metadata rather than emitted by each opener, so
 * `unified_batch_panel.open` can be fired from one place when the panel
 * actually becomes active. `'unknown'` covers opens that carry no metadata —
 * today only the `panel` URL-param restore below.
 */
export type ManageBatchEntrySurface =
  | 'sticky_header_manage_batch_button'
  | 'header_manage_batch_button'
  | 'panel_switcher'
  | 'per_comment_add_to_batch'
  | 'for_you_pending_batch'
  | 'unknown'

export interface ManageBatchPanelMetadata {
  manageBatchEntrySurface?: ManageBatchEntrySurface
}

// Bag of all panel metadata — fields are optional per panel; consumers read the
// subset they care about. Intersection keeps every existing consumer compiling.
export type PanelMetadata = CommentsPanelMetadata & TimelinePanelMetadata & ManageBatchPanelMetadata
export type NullablePanelMetadata = PanelMetadata | null

export interface PanelOpenOptions {
  returnFocusTo?: HTMLElement | null
  metadata?: PanelMetadata
}

interface DockedPanelState {
  activePanel: NullableDockedPanel
  isCompact: boolean
  renderActive: boolean
  metadata: NullablePanelMetadata
  lastPanel: DockedPanel | null
  /**
   * Increments once per deliberate open that makes a panel newly visible.
   *
   * Derived state cannot distinguish a real open from incidental visibility
   * changes: `activePanel` misses a re-open while hidden for resize and flips
   * back when returning to a supported route, and `renderActive` flips during
   * resize. Consumers that must fire exactly once per open (analytics) key off
   * this counter instead. Deliberately not reset by `CLOSE_PANEL` or the
   * supported-route mask so it only ever moves forward.
   */
  openRevision: number
}

type DockedPanelAction =
  | {type: 'OPEN_PANEL'; panel: DockedPanel; isCompact: boolean; metadata: NullablePanelMetadata}
  | {type: 'CLOSE_PANEL'}
  | {type: 'HIDE_FOR_RESIZE'}
  | {type: 'SHOW_ON_RESIZE'}
  | {type: 'TOGGLE_PANEL'; panel: DockedPanel; isCompact: boolean; metadata: NullablePanelMetadata}

const initialState: DockedPanelState = {
  activePanel: null,
  isCompact: false,
  renderActive: false,
  metadata: null,
  lastPanel: null,
  openRevision: 0,
}

function dockedPanelReducer(state: DockedPanelState, action: DockedPanelAction): DockedPanelState {
  switch (action.type) {
    case 'OPEN_PANEL': {
      // Re-opening the panel that is already visible is not a new open. Doing so
      // while it is hidden for resize is, because it makes the panel visible again.
      const alreadyVisible = state.activePanel === action.panel && state.renderActive
      return {
        activePanel: action.panel,
        isCompact: action.isCompact,
        renderActive: true,
        metadata: action.metadata,
        lastPanel: action.panel,
        openRevision: alreadyVisible ? state.openRevision : state.openRevision + 1,
      }
    }
    case 'CLOSE_PANEL':
      return {...initialState, lastPanel: state.lastPanel, openRevision: state.openRevision}
    case 'HIDE_FOR_RESIZE':
      return {...state, renderActive: false}
    case 'SHOW_ON_RESIZE':
      return {...state, renderActive: true}
    case 'TOGGLE_PANEL':
      if (state.activePanel === action.panel && state.renderActive) {
        return {...initialState, lastPanel: state.lastPanel, openRevision: state.openRevision}
      }
      // The remaining branch always makes the panel visible, either by switching
      // panels or by re-showing one hidden for resize.
      return {
        activePanel: action.panel,
        isCompact: action.isCompact,
        renderActive: true,
        metadata: action.metadata,
        lastPanel: action.panel,
        openRevision: state.openRevision + 1,
      }
  }
}

interface DockedPanelActions {
  openPanel: (panel: DockedPanel, options?: PanelOpenOptions) => void
  closePanel: () => void
  togglePanel: (panel: DockedPanel, options?: PanelOpenOptions) => void
}

export interface DockedPanelFocusRefs {
  /** Ref to the close button inside the Panel component */
  closeButtonRef: RefObject<HTMLButtonElement | null>
  /** Ref to the element that should receive focus when the panel closes */
  returnFocusRef: RefObject<HTMLElement | null>
}

// Split contexts to prevent unnecessary re-renders:
// - Components that only need to trigger actions won't re-render when panel state changes
// - Components that need to read state will subscribe to state context
export const DockedPanelStateContext = createContext<DockedPanelState>(initialState)

const defaultActions: DockedPanelActions = {
  openPanel: () => undefined,
  closePanel: () => undefined,
  togglePanel: () => undefined,
}

export const DockedPanelDispatchContext = createContext<DockedPanelActions>(defaultActions)

const defaultFocusRefs: DockedPanelFocusRefs = {
  closeButtonRef: {current: null},
  returnFocusRef: {current: null},
}

export const DockedPanelFocusContext = createContext<DockedPanelFocusRefs>(defaultFocusRefs)

const DEFAULT_ENABLED_PANELS: DockedPanel[] = []

export const DockedPanelEnabledContext = createContext<DockedPanel[]>(DEFAULT_ENABLED_PANELS)

interface DockedPanelProviderProps {
  children: ReactNode
  isOnSupportedRoute?: boolean
  enabledPanels?: DockedPanel[]
}

const PANEL_PARAM = 'panel'
const VALID_PANELS: ReadonlySet<string> = new Set<string>(Object.values(DockedPanel))

function parsePanelParam(value: string | null): DockedPanel | null {
  return value !== null && VALID_PANELS.has(value) ? (value as DockedPanel) : null
}

export function DockedPanelProvider({
  children,
  isOnSupportedRoute = false,
  enabledPanels = DEFAULT_ENABLED_PANELS,
}: DockedPanelProviderProps) {
  const isPanelUrlParamEnabled = isFeatureEnabled('prs_url_search_param_panels')
  const isCopilotChatDockable = isOnSupportedRoute && enabledPanels.includes(DockedPanel.CopilotChat)

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const [state, dispatch] = useReducer(dockedPanelReducer, initialState)

  // Restore panel state after mount, when DOM elements (portal targets,
  // measurement containers) are available. Running in the reducer initializer
  // caused SSR crashes (bare `document` access) and also ran before the portal
  // target was in the DOM, falling through to Dialog mode.
  //
  // An explicit enabled URL panel takes precedence. Otherwise, restore Copilot
  // into its docked host when global navigation persisted the chat as open.
  const hasRestoredRef = useRef(false)
  useEffect(() => {
    if (hasRestoredRef.current) return
    hasRestoredRef.current = true

    const urlPanel = isPanelUrlParamEnabled
      ? parsePanelParam(new URLSearchParams(window.location.search).get(PANEL_PARAM))
      : null
    const enabledUrlPanel = urlPanel && enabledPanels.includes(urlPanel) ? urlPanel : null
    const shouldRestoreCopilotChat =
      !enabledUrlPanel &&
      isCopilotChatDockable &&
      isFeatureEnabled('copilot_chat_app_embedded') &&
      readPersistedChatOpenState()
    const panel = enabledUrlPanel ?? (shouldRestoreCopilotChat ? DockedPanel.CopilotChat : null)

    if (panel) {
      const isWide = measureIsWideViewport()
      dispatch({type: 'OPEN_PANEL', panel, isCompact: !isWide, metadata: null})
    }
  }, [isPanelUrlParamEnabled, enabledPanels, isCopilotChatDockable])

  // Mirror state in refs so memoized action callbacks can read values synchronously
  const stateRef = useRef(state)

  // Signal to the embedded chat v2 surface that this page is docked-capable, so
  // it suppresses its floating panel in favor of the docked side panel. Without
  // a signal that persists while the docked panel is *closed*, a hard refresh on
  // a PR page (where the CopilotChat panel body only mounts while open) leaves
  // the embedded chat unaware the page is docked-capable, so it pops back open
  // as a floating panel. See github/copilot-experiences#12678.
  //
  // This uses a dedicated page-level event (not `copilotDockedPanelReadyEvent`,
  // which the immersive chat also consumes) so it marks the page as docked-only
  // for embedded chat v2 without altering immersive/header behavior. The
  // restoration effect above separately decides whether persisted open state
  // should reopen the docked panel. `pageEnded` on unmount lets floating chat
  // resume on the next non-docked page.
  useEffect(() => {
    if (!isCopilotChatDockable) return
    // Retain the signal in a module store *as well as* dispatching the event:
    // the embedded chat surface mounts asynchronously (payload query + lazy
    // chunk) and routinely misses the one-shot event on a hard refresh.
    publishDockedPageReady()
    document.dispatchEvent(new CustomEvent(copilotDockedPageReadyEvent))
    return () => {
      clearDockedPageReady()
      document.dispatchEvent(new CustomEvent(copilotDockedPageEndedEvent))
    }
  }, [isCopilotChatDockable])

  // Keep stateRef in sync with reducer state after each update.
  // Using useEffect (not render-time assignment) to satisfy React Compiler rules.
  useEffect(() => {
    stateRef.current = state
  }, [state])

  // Sync panel state → URL when URL param syncing is enabled.
  // Uses @github-ui/history's updateUrl (replaceState) to update the URL
  // without triggering React Router's navigation pipeline, soft-nav progress
  // bars, or unnecessary re-renders.
  useEffect(() => {
    if (!isPanelUrlParamEnabled) return
    const urlParams = new URLSearchParams(window.location.search)
    const currentUrlPanel = parsePanelParam(urlParams.get(PANEL_PARAM))
    if (state.activePanel === currentUrlPanel) return

    if (state.activePanel) {
      urlParams.set(PANEL_PARAM, state.activePanel)
    } else {
      urlParams.delete(PANEL_PARAM)
    }
    const search = urlParams.toString()
    const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
    updateUrl(newUrl)
  }, [state.activePanel, isPanelUrlParamEnabled])

  // Memoize actions object to keep dispatch context value stable
  // This ensures components using only useDockedPanelActions() won't re-render on state changes
  const actions = useMemo<DockedPanelActions>(
    () => ({
      openPanel: (panel: DockedPanel, options?: PanelOpenOptions) => {
        if (options?.returnFocusTo) returnFocusRef.current = options.returnFocusTo
        const isWide = measureIsWideViewport()
        dispatch({type: 'OPEN_PANEL', panel, isCompact: !isWide, metadata: options?.metadata ?? null})
        // Only focuses the close button if the panel will be visible (wide viewport).
        // On compact viewports the panel shows as a dialog which manages its own focus.
        if (isWide) {
          requestAnimationFrame(() => closeButtonRef.current?.focus())
        }
      },
      closePanel: () => {
        dispatch({type: 'CLOSE_PANEL'})
      },
      togglePanel: (panel: DockedPanel, options?: PanelOpenOptions) => {
        if (options?.returnFocusTo) returnFocusRef.current = options.returnFocusTo
        const isWide = measureIsWideViewport()
        dispatch({type: 'TOGGLE_PANEL', panel, isCompact: !isWide, metadata: options?.metadata ?? null})
        // Focus the close button when opening/switching (not closing) in wide mode
        if (stateRef.current.activePanel !== panel && isWide) {
          requestAnimationFrame(() => closeButtonRef.current?.focus())
        }
      },
    }),
    [returnFocusRef, closeButtonRef],
  )

  // Auto-close the panel when the container shrinks below the breakpoint while in docked (non-compact) mode.
  // A panel opened in compact (dialog) mode is unaffected.
  //
  // The handler is debounced + rAF-coalesced so the HIDE_FOR_RESIZE/SHOW_ON_RESIZE
  // dispatch only fires once the user finishes resizing. Mounting/unmounting the
  // side panel mid-gesture causes a full content-column reflow on every dispatch,
  // so deferring to resize-end is critical for staying at 30-60fps during drag.
  //
  // Read the cached border-box inline size from the ResizeObserver entry
  // (matching what `measureIsWideViewport`'s `getBoundingClientRect` returns on
  // the open path). Using `contentRect.width` here would measure the content
  // box, which shifts when the `[data-side-panel-open]` styles flip
  // `padding-right`.
  useEffect(() => {
    let rafId: number | null = null
    let debounceId: ReturnType<typeof setTimeout> | null = null
    let lastContainerWidth: number | null = null

    function handleResize(entries: ResizeObserverEntry[]) {
      const entry = entries[0]
      if (entry) {
        // Prefer borderBoxSize (the actual element box width) over contentRect.width
        // (content-box, padding-excluded) so we measure the same thing on both
        // the open and auto-hide paths. Pre-spec implementations (Chrome ≤83,
        // Firefox ≤68) exposed borderBoxSize as a single object instead of an
        // array; handle both shapes so those engines still get the border-box
        // reading. Falls back to contentRect.width only if borderBoxSize is
        // entirely absent.
        const borderBoxRaw = entry.borderBoxSize
        const borderBox = Array.isArray(borderBoxRaw) ? borderBoxRaw[0] : borderBoxRaw
        lastContainerWidth = borderBox ? borderBox.inlineSize : entry.contentRect.width
      }
      if (debounceId !== null) clearTimeout(debounceId)
      debounceId = setTimeout(() => {
        debounceId = null
        rafId = requestAnimationFrame(() => {
          rafId = null
          measureAndDispatch()
        })
      }, 150)
    }

    function measureAndDispatch() {
      if (!stateRef.current.activePanel) return

      const isWide = lastContainerWidth !== null ? isWideWidth(lastContainerWidth) : measureIsWideViewport()

      if (!stateRef.current.isCompact && !isWide) {
        dispatch({type: 'HIDE_FOR_RESIZE'})
      }

      if (stateRef.current.renderActive === false && isWide) {
        dispatch({type: 'SHOW_ON_RESIZE'})
      }
    }

    const container = document.getElementById(DIFF_COMPARISON_VIEWER_CONTAINER_ID)
    let observer: ResizeObserver | null = null
    if (container) {
      observer = new ResizeObserver(handleResize)
      observer.observe(container)
    }

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      if (debounceId !== null) clearTimeout(debounceId)
      observer?.disconnect()
    }
  }, [])

  // Stable ref object - refs don't change identity so this is safe to memoize once
  const focusRefs = useMemo<DockedPanelFocusRefs>(() => ({closeButtonRef, returnFocusRef}), [])

  // Only expose the panel state when on a supported route (files changed).
  // On other routes the panel is effectively closed, hiding the sidebar.
  // `openRevision` is carried through the mask so leaving and returning to a
  // supported route is not mistaken for a re-open by consumers keyed off it.
  const effectiveState = useMemo(
    () => (isOnSupportedRoute ? state : {...initialState, openRevision: state.openRevision}),
    [isOnSupportedRoute, state],
  )

  return (
    <DockedPanelEnabledContext value={enabledPanels}>
      <DockedPanelStateContext value={effectiveState}>
        <DockedPanelDispatchContext value={actions}>
          <DockedPanelFocusContext value={focusRefs}>{children}</DockedPanelFocusContext>
        </DockedPanelDispatchContext>
      </DockedPanelStateContext>
    </DockedPanelEnabledContext>
  )
}

/** Returns memoized actions to control the docked panel - stable reference, won't cause re-renders */
export function useDockedPanelActions(): DockedPanelActions {
  return use(DockedPanelDispatchContext)
}

/** Returns whether any docked panel is currently open on a supported route (files changed) */
export function useIsDockedPanelOpen(dockedPanelType?: DockedPanel | undefined): boolean {
  const context = use(DockedPanelStateContext)
  if (dockedPanelType) {
    return context.activePanel === dockedPanelType && context.renderActive
  }

  return context.activePanel !== null && context.renderActive
}

export function useActiveDockedPanel(): NullableDockedPanel {
  return use(DockedPanelStateContext).activePanel
}

/**
 * Returns a counter that increments once per deliberate open that makes a panel
 * newly visible. Use this, not `activePanel`/`renderActive`, when something must
 * happen exactly once per open. See `DockedPanelState.openRevision`.
 */
export function useDockedPanelOpenRevision(): number {
  return use(DockedPanelStateContext).openRevision
}

/** Returns shared focus refs for managing focus between panel openers and the close button */
export function useDockedPanelFocusRefs(): DockedPanelFocusRefs {
  return use(DockedPanelFocusContext)
}

/** Returns whether the panel is in compact mode (container < 1012px) and showing as a dialog overlay */
export function useIsDockedPanelCompact(): boolean {
  return use(DockedPanelStateContext).isCompact
}

export function useEnabledDockedPanels(): DockedPanel[] {
  return use(DockedPanelEnabledContext)
}

/** Returns the metadata passed to the most recent openPanel/togglePanel call, or null */
export function useDockedPanelMetadata(): NullablePanelMetadata {
  return use(DockedPanelStateContext).metadata
}

/** Returns the last panel that was opened, or null if none */
export function useLastPanel(): DockedPanel | null {
  return use(DockedPanelStateContext).lastPanel
}
